import { useCallback } from "react";
import { Linking, Pressable } from "react-native";
import { useNavigation } from "expo-router";
import { View, Text, ScreenContainer, Switch } from "@src/components";
import { Icon } from "@src/components/Icon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { PermissionDeniedError, useDeviceToken } from "@visioni/a3s-notifications/app";
import { getDeviceTokenApi } from "@src/features/notifications";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { appStorage, AppStorageKeys } from "@src/services/app-storage.native";

export default function AppNotificationsScreen() {
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();
  const { theme } = useUnistyles();
  const deviceTokenApi = getDeviceTokenApi();

  navigation.setOptions({
    title: "App Notifications",
    headerBackButtonDisplayMode: "minimal",
    headerLeft,
  });

  const { isRegistered, register, unregister } = useDeviceToken({ deviceTokenApi });
  const data = isRegistered.data ?? {
    permission: "undetermined" as const,
    registered: appStorage.get<boolean>(AppStorageKeys.PUSH_NOTIFICATIONS_ENABLED) ?? false,
  };

  const isEnabled = data.registered;
  const isDeniedAtOsLevel = data.permission === "denied";

  const isBusy = register.isPending || unregister.isPending;

  const handleToggle = useCallback(() => {
    if (isEnabled) {
      unregister.mutate(undefined, {
        onSuccess: () => {
          appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_ENABLED, false);
          showSuccessToast({ message: "Notifications disabled" });
        },
      });
    } else {
      register.mutate(undefined, {
        onSuccess: () => {
          appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_ENABLED, true);
          showSuccessToast({ message: "Notifications enabled" });
        },
        onError: (error) => {
          if (error instanceof PermissionDeniedError) {
            showErrorToast({ message: "Permission not granted. Enable in Settings." });
          } else {
            showErrorToast({ message: "Something went wrong. Please try again." });
          }
        },
      });
    }
  }, [isEnabled, register, unregister]);

  const statusText =
    isRegistered.isLoading ? "Loading..."
    : isDeniedAtOsLevel ? "Blocked by system"
    : isEnabled ? "Enabled"
    : "Disabled";

  return (
    <ScreenContainer edges={["bottom"]}>
      <View flex={1} px="5xl" pt="4xl">
        <View bg="surfaceCard" style={styles.card}>
          <View style={styles.row}>
            <View flex={1}>
              <Text textStyle="md/medium">Push Notifications</Text>
              <Text textStyle="xs/regular" color="textMuted" mt="xs">
                {statusText}
              </Text>
            </View>
            <Switch
              active={isEnabled}
              onToggle={handleToggle}
              disabled={isBusy || isRegistered.isLoading || isDeniedAtOsLevel}
              size="medium"
            />
          </View>
        </View>

        {isDeniedAtOsLevel && (
          <Pressable style={styles.card} onPress={() => void Linking.openSettings()}>
            <View bg="surfaceCard" style={styles.row}>
              <View flex={1}>
                <Text textStyle="sm/medium">Open System Settings</Text>
                <Text textStyle="xs/regular" color="textMuted" mt="xs">
                  Enable push notifications in system settings. Tap to open.
                </Text>
              </View>
              <Icon name="chevronRight" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
            </View>
          </Pressable>
        )}

        <View mt="4xl" px="md">
          <Text textStyle="xs/regular" color="textMuted">
            When enabled, you will receive push notifications for alarm activations and other in-app activity.
          </Text>
        </View>
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create((theme) => ({
  card: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: "hidden",
    marginBottom: theme.spacing["3xl"],
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
  },
}));
